import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmitAction } from '~/utils/form';
import { Button } from "@repo/ui/components/button";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod/v4";
import { Alert, AlertDescription } from "@repo/ui/components/alert";
import { TextField } from "@repo/ui/components/text-field";
import { Form, useForm } from "@repo/ui/components/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/dialog";


export default function FormContact() {
  const fetcher = useSubmitAction();
  const { t } = useTranslation("contact");
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string(t("error.required", { ns: "common" }))
          .min(1, t("error.required", { ns: "common" }) as string)
          .max(100, t("error.max", { ns: "common", max: 100 }) as string),
        phone: z
          .string(t("error.required", { ns: "common" }))
          .min(1, t("error.required", { ns: "common" }) as string)
          .max(11, t("error.max", { ns: "common", max: 11 }) as string)
          .regex(/^\d+$/, t("error.invalid_phone", { ns: "common" }) as string),
        message: z.string().optional().or(z.literal("")),
      }),
    [t],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", phone: "", message: "" },
    mode: "onChange",
  });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      if (!fetcher.data.ok) {
        setSuccessMsg(null);
        setErrorMsg("Gửi không thành công. Vui lòng thử lại.");
      } else {
        setErrorMsg(null);
        setSuccessMsg("Đăng ký thành công. Chúng tôi sẽ liên hệ sớm nhất!");
        setSuccessOpen(true);
        // Reset form values when closing success dialog as well for safety
        form.reset();
      }
    }
  }, [fetcher.state, fetcher.data]);
  const onSubmit = async (data: any) => {
    setErrorMsg(null);
    await fetcher.handleSubmitToServer({
      FullName: data.name,
      Phone: data.phone,
      Note: data.message || null,
    });
  };

  const handleCloseModal = (value: boolean) => {
    setSuccessOpen(value);
    if (!value) {
      setSuccessMsg(null);
    }
  };
  return (
    <div className="flex flex-col md:flex-row gap-10 p-5 md:p-10 bg-[url('/image/landing_page/background/banner_4.png')] bg-no-repeat bg-cover rounded-[20px]">
      <div className="">
        <div className="text-4xl md:text-5xl md:leading-[60px] font-helve font-bold text-white pb-5">
          Đừng lo, Kland ở đây để chăm sóc bạn tận tình
        </div>
        <p className="text-2xl font-normal leading-8 text-white font-helve">
          Dù bạn đang tìm tổ ấm hay cơ hội đầu tư, Kland sẽ lắng nghe mong muốn,
          tìm kiếm và gợi ý giải pháp chính xác đến từng nhu cầu riêng biệt.
        </p>
      </div>
      <Form {...form}>
        <form
          id="contact-form"
          className="grid gap-4 mt-5  md:min-w-[454px]"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              name="name"
              label={t("form.fields.name.label")}
              placeholder={t("form.fields.name.placeholder")}
              propsLabel={{
                className: "text-white",
              }}
            />
            <TextField
              name="phone"
              label={t("form.fields.phone.label")}
              placeholder={t("form.fields.phone.placeholder")}
              propsLabel={{
                className: "text-white",
              }}
            />
          </div>
          <TextField
            name="message"
            label={"Nội dung liên hệ"}
            placeholder={"Nhập nội dung muốn liên hệ"}
            propsLabel={{
              className: "text-white",
            }}
          />
          <Button type="submit" variant={"secondary"} size={"lg"}>
            Nhận tư vấn ngay
          </Button>
          {errorMsg ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          ) : null}
        </form>
      </Form>
      {successMsg ? (
        <Dialog open={successOpen} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Thông báo</DialogTitle>
            </DialogHeader>
            <Alert className="border-0 bg-primary-50 text-primary">
              <AlertDescription>{successMsg}</AlertDescription>
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => handleCloseModal(false)}>Đóng</Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
